/**
 * Convex Report Builder
 *
 * A reusable, elegant filter builder + preview UI for Convex data.
 * Supports multiple filter types, presets, and Excel export.
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import type { GridApi, ColDef } from '@ag-grid-community/core';
import { useMutation, useQuery } from 'convex/react';
import { format } from 'date-fns';
import {
  Filter,
  Plus,
  Trash2,
  X,
  ChevronDown,
  RotateCcw,
  Loader2,
  FileSpreadsheet,
  Check,
  Save,
  Bookmark,
  MoreHorizontal,
  Star,
  Pencil,
  Columns3,
  Eye,
  EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { utils, writeFile } from 'xlsx';
import AGDataGrid from '@/components/ag-data-grid';
import { Chip, ChipGroup, FacetLabel } from '@/components/chip-group';
import { CopyCountButton } from '@/components/copy-count-button';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type {
  ConvexReportBuilderProps,
  FilterCondition,
  FilterField,
  FilterGroup,
  FilterOperator,
  FilterPreset,
  ReportColumn,
} from './types';
import type { Id } from '../../../convex/_generated/dataModel';
import { api } from '../../../convex/_generated/api';
import {
  applyFilters,
  createCondition,
  createEmptyFilterGroup,
  removeCondition,
  updateCondition,
  countActiveFilters,
} from './filter-utils';
import { getDefaultOperators, OPERATOR_LABELS } from './types';

// ============================================================================
// Filter Condition Row Component
// ============================================================================

interface FilterConditionRowProps {
  condition: FilterCondition;
  field: FilterField | undefined;
  allFields: FilterField[];
  onUpdate: (updates: Partial<FilterCondition>) => void;
  onRemove: () => void;
}

function FilterConditionRow({
  condition,
  field,
  allFields,
  onUpdate,
  onRemove,
}: FilterConditionRowProps) {
  const operators = field
    ? field.operators || getDefaultOperators(field.type)
    : ['equals'];

  const isNullOperator = condition.operator === 'is_null' || condition.operator === 'is_not_null';

  const renderValueInput = () => {
    if (field?.type === 'enum' && field.enumOptions) {
      if (field.multiple || condition.operator === 'in' || condition.operator === 'not_in') {
        return (
          <EnumMultiSelect
            options={field.enumOptions}
            value={Array.isArray(condition.value) ? condition.value : []}
            onChange={(value) => onUpdate({ value })}
          />
        );
      }
      return (
        <Select
          value={String(condition.value || '')}
          onValueChange={(value) => onUpdate({ value })}
        >
          <SelectTrigger className="h-8 w-40 text-xs">
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {field.enumOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="text-xs">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (field?.type === 'boolean') {
      return (
        <Select
          value={String(condition.value)}
          onValueChange={(value) => onUpdate({ value: value === 'true' })}
        >
          <SelectTrigger className="h-8 w-24 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true" className="text-xs">Yes</SelectItem>
            <SelectItem value="false" className="text-xs">No</SelectItem>
          </SelectContent>
        </Select>
      );
    }

    if (field?.type === 'date') {
      return (
        <Input
          type="date"
          value={condition.value ? format(new Date(condition.value as number), 'yyyy-MM-dd') : ''}
          onChange={(e) => {
            const date = e.target.value ? new Date(e.target.value).getTime() : null;
            onUpdate({ value: date });
          }}
          className="h-8 w-36 text-xs"
        />
      );
    }

    if (field?.type === 'number') {
      return (
        <Input
          type="number"
          value={condition.value as string}
          onChange={(e) => onUpdate({ value: e.target.value ? Number(e.target.value) : '' })}
          placeholder="Value"
          className="h-8 w-24 text-xs"
        />
      );
    }

    return (
      <Input
        type="text"
        value={condition.value as string}
        onChange={(e) => onUpdate({ value: e.target.value })}
        placeholder="Value"
        className="h-8 w-40 text-xs"
      />
    );
  };

  return (
    <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-2">
      {/* Enable/Disable toggle */}
      <Checkbox
        checked={condition.enabled}
        onCheckedChange={(checked) => onUpdate({ enabled: !!checked })}
        className="size-4"
      />

      {/* Field selector */}
      <Select
        value={condition.field}
        onValueChange={(value) => {
          const newField = allFields.find((f) => f.field === value);
          const newOps = newField
            ? newField.operators || getDefaultOperators(newField.type)
            : ['equals'];
          onUpdate({
            field: value,
            operator: newOps[0],
            value: newField?.type === 'enum' && newField.multiple ? [] : '',
          });
        }}
      >
        <SelectTrigger className="h-8 w-36 text-xs">
          <SelectValue placeholder="Field" />
        </SelectTrigger>
        <SelectContent>
          {allFields.map((f) => (
            <SelectItem key={f.field} value={f.field} className="text-xs">
              {f.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Operator selector */}
      <Select
        value={condition.operator}
        onValueChange={(value) => onUpdate({ operator: value as FilterOperator })}
      >
        <SelectTrigger className="h-8 w-32 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {operators.map((op) => (
            <SelectItem key={op} value={op} className="text-xs">
              {OPERATOR_LABELS[op]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Value input - varies by field type */}
      {!isNullOperator && renderValueInput()}

      {/* Remove button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onRemove}
        className="size-8 p-0 text-muted-foreground hover:text-destructive"
      >
        <X className="size-4" />
      </Button>
    </div>
  );
}

// ============================================================================
// Enum Multi-Select Component
// ============================================================================

interface EnumMultiSelectProps {
  options: Array<{ value: string; label: string }>;
  value: string[];
  onChange: (value: string[]) => void;
}

function EnumMultiSelect({ options, value, onChange }: EnumMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleValue = (val: string) => {
    if (value.includes(val)) {
      onChange(value.filter((v) => v !== val));
    } else {
      onChange([...value, val]);
    }
  };

  const selectedLabels = value
    .map((v) => options.find((o) => o.value === v)?.label)
    .filter(Boolean);

  // Filter options by search query
  const filteredOptions = searchQuery
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : options;

  return (
    <Popover
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen)
          setSearchQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-40 justify-between text-xs font-normal"
        >
          <span className="truncate">
            {selectedLabels.length > 0
              ? (selectedLabels.length === 1
                  ? selectedLabels[0]
                  : `${selectedLabels.length} selected`)
              : 'Select...'}
          </span>
          <ChevronDown className="ml-1 size-3 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <div className="border-b p-2">
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-7 text-xs"
            autoFocus
          />
        </div>
        <div className="max-h-64 overflow-auto p-2">
          {filteredOptions.length === 0
            ? (
                <div className="py-2 text-center text-xs text-muted-foreground">
                  No results found
                </div>
              )
            : (
                <div className="flex flex-col gap-1">
                  {filteredOptions.map((opt) => (
                    <label
                      key={opt.value}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-muted"
                    >
                      <Checkbox
                        checked={value.includes(opt.value)}
                        onCheckedChange={() => toggleValue(opt.value)}
                        className="size-3.5"
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ============================================================================
// Quick Filter Chips Component
// ============================================================================

interface QuickFilterChipsProps {
  filterFields: FilterField[];
  filters: FilterGroup;
  onToggleValue: (field: string, value: string) => void;
}

function QuickFilterChips({ filterFields, filters, onToggleValue }: QuickFilterChipsProps) {
  // Get enum fields that have multiple options for quick filtering
  // Allow up to 30 options for chips (to include registrars)
  const enumFields = filterFields.filter(
    (f) => f.type === 'enum' && f.enumOptions && f.enumOptions.length <= 30,
  );

  if (enumFields.length === 0)
    return null;

  return (
    <div className="flex flex-wrap gap-4">
      {enumFields.map((field) => {
        // Find existing condition for this field
        const existingCondition = filters.conditions.find(
          (c) => c.field === field.field && (c.operator === 'in' || c.operator === 'equals'),
        );
        const selectedValues: string[] = existingCondition
          ? (Array.isArray(existingCondition.value)
              ? existingCondition.value
              : [String(existingCondition.value)])
          : [];

        return (
          <ChipGroup
            key={field.field}
            label={field.groupLabel || field.label}
            options={field.enumOptions ?? []}
            selected={selectedValues}
            onToggle={(value) => onToggleValue(field.field, value)}
          />
        );
      })}
    </div>
  );
}

// ============================================================================
// Column Visibility Chips Component
// ============================================================================

interface ColumnVisibilityChipsProps<T> {
  columns: ReportColumn<T>[];
  visibleColumns: Set<string>;
  onToggleColumn: (field: string) => void;
  onShowAll: () => void;
  onHideAll: () => void;
}

function ColumnVisibilityChips<T>({
  columns,
  visibleColumns,
  onToggleColumn,
  onShowAll,
  onHideAll,
}: ColumnVisibilityChipsProps<T>) {
  const allVisible = visibleColumns.size === columns.length;
  const noneVisible = visibleColumns.size === 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <FacetLabel>Visible Columns</FacetLabel>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={onShowAll}
            disabled={allVisible}
            className="h-6 gap-1 px-2 text-[10px]"
          >
            <Eye className="size-3" />
            Show All
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onHideAll}
            disabled={noneVisible}
            className="h-6 gap-1 px-2 text-[10px]"
          >
            <EyeOff className="size-3" />
            Hide All
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {columns.map((col) => {
          const isVisible = visibleColumns.has(col.field);
          return (
            <Chip key={col.field} selected={isVisible} onClick={() => onToggleColumn(col.field)}>
              {isVisible
                ? (
                    <Eye className="size-3" />
                  )
                : (
                    <EyeOff className="size-3 opacity-50" />
                  )}
              {col.headerName}
            </Chip>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Save Preset Dialog Component
// ============================================================================

interface SavePresetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (name: string, description: string) => void;
  isSaving: boolean;
}

function SavePresetDialog({ open, onOpenChange, onSave, isSaving }: SavePresetDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleSave = () => {
    if (!name.trim()) {
      toast.error('Please enter a preset name');
      return;
    }
    onSave(name.trim(), description.trim());
  };

  // Reset form when dialog opens
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setName('');
      setDescription('');
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Save Preset</DialogTitle>
          <DialogDescription>
            Save your current filter configuration as a reusable preset.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="preset-name">Name</Label>
            <Input
              id="preset-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Live .Bank Domains"
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="preset-description">Description (optional)</Label>
            <Textarea
              id="preset-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this preset filters for..."
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !name.trim()}>
            {isSaving
              ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Saving...
                  </>
                )
              : (
                  <>
                    <Save className="mr-2 size-4" />
                    Save Preset
                  </>
                )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Saved Preset Type
// ============================================================================

interface SavedPreset {
  _id: Id<'reportPresets'>;
  name: string;
  description?: string;
  isBuiltIn: boolean;
  isBookmarked?: boolean;
  builtInPresetId?: string; // If this is a bookmarked copy of a built-in preset
  filters: FilterGroup;
  createdAt: number;
}

// ============================================================================
// Presets Combobox Component
// ============================================================================

interface PresetsComboboxProps {
  builtInPresets: FilterPreset[];
  savedPresets: SavedPreset[] | undefined;
  selectedPresetName: string | null;
  onApplyBuiltIn: (preset: FilterPreset) => void;
  onApplySaved: (preset: SavedPreset) => void;
  onEdit: (preset: SavedPreset) => void;
  onDelete: (presetId: Id<'reportPresets'>) => void;
  onToggleBookmark: (presetId: Id<'reportPresets'>) => void;
  onToggleBuiltInBookmark: (preset: FilterPreset) => void;
}

function PresetsCombobox({
  builtInPresets,
  savedPresets,
  selectedPresetName,
  onApplyBuiltIn,
  onApplySaved,
  onEdit,
  onDelete,
  onToggleBookmark,
  onToggleBuiltInBookmark,
}: PresetsComboboxProps) {
  const [open, setOpen] = useState(false);

  // Get IDs of built-in presets that have been bookmarked
  const bookmarkedBuiltInIds = new Set(
    savedPresets?.filter((p) => p.builtInPresetId).map((p) => p.builtInPresetId) || [],
  );

  // Separate saved presets into categories (already sorted by backend)
  const bookmarkedPresets = savedPresets?.filter((p) => p.isBookmarked) || [];
  const customPresets = savedPresets?.filter((p) => !p.isBookmarked && !p.builtInPresetId) || [];

  // Filter out built-in presets that have been bookmarked (they appear in bookmarked section)
  const unbookmarkedBuiltInPresets = builtInPresets.filter((p) => !bookmarkedBuiltInIds.has(p.id));

  const hasPresets = builtInPresets.length > 0 || (savedPresets && savedPresets.length > 0);

  const renderSavedPresetItem = (preset: SavedPreset) => (
    <CommandItem
      key={preset._id}
      onSelect={() => {
        onApplySaved(preset);
        setOpen(false);
      }}
      className="group flex items-center justify-between"
    >
      <div className="flex items-center gap-2">
        {preset.isBookmarked
          ? (
              <Star className="size-4 fill-amber-500 text-amber-500" />
            )
          : (
              <Bookmark className="size-4 text-primary" />
            )}
        <div className="flex flex-col">
          <span>{preset.name}</span>
          {preset.description && (
            <span className="text-xs text-muted-foreground">{preset.description}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
        <Button
          variant="ghost"
          size="icon"
          className={cn('size-6', preset.isBookmarked && 'text-amber-500')}
          onClick={(e) => {
            e.stopPropagation();
            onToggleBookmark(preset._id);
          }}
          title={preset.isBookmarked ? 'Remove bookmark' : 'Bookmark'}
        >
          <Star className={cn('size-3', preset.isBookmarked && 'fill-current')} />
        </Button>
        {!preset.builtInPresetId && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="size-6"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(preset);
                setOpen(false);
              }}
              title="Edit"
            >
              <Pencil className="size-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-6 hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(preset._id);
              }}
              title="Delete"
            >
              <Trash2 className="size-3" />
            </Button>
          </>
        )}
      </div>
    </CommandItem>
  );

  const renderBuiltInPresetItem = (preset: FilterPreset) => (
    <CommandItem
      key={preset.id}
      onSelect={() => {
        onApplyBuiltIn(preset);
        setOpen(false);
      }}
      className="group flex items-center justify-between"
    >
      <div className="flex items-center gap-2">
        <Filter className="size-4 text-muted-foreground" />
        <div className="flex flex-col">
          <span>{preset.name}</span>
          {preset.description && (
            <span className="text-xs text-muted-foreground">{preset.description}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
        <Button
          variant="ghost"
          size="icon"
          className="size-6"
          onClick={(e) => {
            e.stopPropagation();
            onToggleBuiltInBookmark(preset);
          }}
          title="Bookmark"
        >
          <Star className="size-3" />
        </Button>
      </div>
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          {selectedPresetName
            ? (
                <>
                  <Star className="size-3.5 fill-amber-500 text-amber-500" />
                  <span className="max-w-32 truncate">{selectedPresetName}</span>
                </>
              )
            : (
                <>
                  <Star className="size-3.5" />
                  <span>Apply Preset</span>
                </>
              )}
          <ChevronDown className="size-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search presets..." />
          <CommandList>
            <CommandEmpty>No presets found.</CommandEmpty>
            {/* Bookmarked presets first */}
            {bookmarkedPresets.length > 0 && (
              <CommandGroup heading="Bookmarked">
                {bookmarkedPresets.map((preset) => renderSavedPresetItem(preset))}
              </CommandGroup>
            )}
            {/* Custom presets second */}
            {customPresets.length > 0 && (
              <>
                {bookmarkedPresets.length > 0 && <CommandSeparator />}
                <CommandGroup heading="Custom">
                  {customPresets.map((preset) => renderSavedPresetItem(preset))}
                </CommandGroup>
              </>
            )}
            {/* Default/Built-in presets last (from code, not DB) */}
            {unbookmarkedBuiltInPresets.length > 0 && (
              <>
                {(bookmarkedPresets.length > 0 || customPresets.length > 0) && <CommandSeparator />}
                <CommandGroup heading="Default">
                  {unbookmarkedBuiltInPresets.map((preset) => renderBuiltInPresetItem(preset))}
                </CommandGroup>
              </>
            )}
            {!hasPresets && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No presets available
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ============================================================================
// Edit Preset Dialog Component
// ============================================================================

interface EditPresetDialogProps {
  preset: SavedPreset | null;
  onClose: () => void;
  onSave: (presetId: Id<'reportPresets'>, name: string, description: string) => void;
  isUpdating: boolean;
}

function EditPresetDialog({ preset, onClose, onSave, isUpdating }: EditPresetDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Reset form when preset changes
  useEffect(() => {
    if (preset) {
      setName(preset.name);
      setDescription(preset.description || '');
    }
  }, [preset]);

  const handleSave = () => {
    if (preset && name.trim()) {
      onSave(preset._id, name.trim(), description.trim());
    }
  };

  return (
    <Dialog open={!!preset} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Preset</DialogTitle>
          <DialogDescription>
            Update the name and description of your saved preset.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="edit-preset-name">Name</Label>
            <Input
              id="edit-preset-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Live .Bank Domains"
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="edit-preset-description">Description (optional)</Label>
            <Textarea
              id="edit-preset-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this preset filters for..."
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isUpdating || !name.trim()}>
            {isUpdating
              ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Saving...
                  </>
                )
              : (
                  <>
                    <Save className="mr-2 size-4" />
                    Save Changes
                  </>
                )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Preset Browser Dialog Component
// ============================================================================

interface PresetBrowserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  presets: SavedPreset[] | undefined;
  builtInPresets: FilterPreset[];
  onApply: (filters: FilterGroup, name: string) => void;
  onDelete: (presetId: Id<'reportPresets'>) => void;
  isDeleting: boolean;
}

function PresetBrowserDialog({
  open,
  onOpenChange,
  presets,
  builtInPresets,
  onApply,
  onDelete,
  isDeleting,
}: PresetBrowserDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<Id<'reportPresets'> | null>(null);

  // Combine built-in and saved presets
  const allPresets = useMemo(() => {
    const builtIn = builtInPresets.map((p) => ({
      id: p.id,
      name: p.name,
      description: p.description,
      isBuiltIn: true,
      filters: p.filters,
      createdAt: 0,
    }));

    const saved = (presets || []).map((p) => ({
      id: p._id,
      name: p.name,
      description: p.description,
      isBuiltIn: p.isBuiltIn,
      filters: p.filters,
      createdAt: p.createdAt,
    }));

    return [...builtIn, ...saved];
  }, [builtInPresets, presets]);

  // Filter presets by search query
  const filteredPresets = useMemo(() => {
    if (!searchQuery.trim())
      return allPresets;
    const query = searchQuery.toLowerCase();
    return allPresets.filter(
      (p) =>
        p.name.toLowerCase().includes(query)
        || p.description?.toLowerCase().includes(query),
    );
  }, [allPresets, searchQuery]);

  const handleApply = (preset: (typeof allPresets)[0]) => {
    onApply(preset.filters, preset.name);
    onOpenChange(false);
  };

  const handleDelete = (presetId: Id<'reportPresets'>) => {
    onDelete(presetId);
    setDeleteConfirm(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Browse Presets</DialogTitle>
          <DialogDescription>
            Select a preset to apply its filters, or manage your saved presets.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <Input
            type="search"
            placeholder="Search presets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <div className="max-h-80 overflow-y-auto">
            {filteredPresets.length === 0
              ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    No presets found
                  </div>
                )
              : (
                  <div className="flex flex-col gap-1">
                    {filteredPresets.map((preset) => (
                      <div
                        key={preset.id}
                        className="group flex items-center justify-between rounded-md border p-3 hover:bg-muted/50"
                      >
                        <button
                          className="flex flex-1 flex-col items-start gap-0.5 text-left"
                          onClick={() => handleApply(preset)}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{preset.name}</span>
                            {preset.isBuiltIn && (
                              <Badge variant="secondary" className="text-[10px]">
                                <Star className="mr-1 size-3" />
                                Built-in
                              </Badge>
                            )}
                          </div>
                          {preset.description && (
                            <span className="text-xs text-muted-foreground">
                              {preset.description}
                            </span>
                          )}
                        </button>
                        {!preset.isBuiltIn && typeof preset.id === 'string' && preset.id.startsWith('j') && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="size-8 p-0 opacity-0 group-hover:opacity-100"
                              >
                                <MoreHorizontal className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleApply(preset)}
                              >
                                <Check className="mr-2 size-4" />
                                Apply
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => setDeleteConfirm(preset.id as Id<'reportPresets'>)}
                              >
                                <Trash2 className="mr-2 size-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    ))}
                  </div>
                )}
          </div>
        </div>
      </DialogContent>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Preset?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. The preset will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
              disabled={isDeleting}
            >
              {isDeleting
                ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />
                      Deleting...
                    </>
                  )
                : (
                    'Delete'
                  )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

// ============================================================================
// Main Report Builder Component
// ============================================================================

interface ExtendedConvexReportBuilderProps<T> extends ConvexReportBuilderProps<T> {
  // Report type identifier for saving presets (e.g., 'monitor')
  reportType: string;
}

export function ConvexReportBuilder<T extends Record<string, unknown>>({
  data,
  isLoading,
  columns,
  filterFields,
  presets: builtInPresets = [],
  title,
  description,
  defaultFilename = 'report',
  getRowId,
  reportType,
}: ExtendedConvexReportBuilderProps<T>) {
  const [filters, setFilters] = useState<FilterGroup>(createEmptyFilterGroup());
  const [searchQuery, setSearchQuery] = useState('');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [browserDialogOpen, setBrowserDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingPreset, setEditingPreset] = useState<SavedPreset | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedPresetName, setSelectedPresetName] = useState<string | null>(null);
  const gridApiRef = useRef<GridApi<T> | null>(null);

  // Column visibility state - default to all columns visible
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() =>
    new Set(columns.map((col) => col.field)),
  );

  // Convex queries and mutations for presets
  const savedPresets = useQuery(api.reportPresets.listPresets, { reportType });
  const savePresetMutation = useMutation(api.reportPresets.savePreset);
  const deletePresetMutation = useMutation(api.reportPresets.deletePreset);
  const updatePresetMutation = useMutation(api.reportPresets.updatePreset);
  const toggleBookmarkMutation = useMutation(api.reportPresets.toggleBookmark);
  const bookmarkBuiltInMutation = useMutation(api.reportPresets.bookmarkBuiltInPreset);

  // Apply filters to data
  const filteredData = useMemo(() => {
    if (!data)
      return [];
    return applyFilters(data, filters);
  }, [data, filters]);

  // Column definitions for AG Grid - filtered by visibility
  const columnDefs = useMemo((): ColDef<T>[] => {
    return columns
      .filter((col) => visibleColumns.has(col.field))
      .map((col) => ({
        field: col.field,
        headerName: col.headerName,
        width: col.width,
        minWidth: col.minWidth || 80,
        flex: col.flex,
        sortable: col.sortable ?? true,
        filter: col.filter ?? true,
        valueFormatter: col.valueFormatter
          ? (params) => col.valueFormatter!(params.value, params.data as T)
          : undefined,
        cellRenderer: col.cellRenderer
          ? (params: { value: unknown; data: T }) => col.cellRenderer!(params.value, params.data)
          : undefined,
      }));
  }, [columns, visibleColumns]);

  // Handle quick filter chip toggle
  const handleQuickFilterToggle = useCallback((field: string, value: string) => {
    setFilters((prev) => {
      const existingIdx = prev.conditions.findIndex(
        (c) => c.field === field && (c.operator === 'in' || c.operator === 'equals'),
      );

      if (existingIdx === -1) {
        // Create new condition
        const newCondition = createCondition(field, 'in', [value]);
        return { ...prev, conditions: [...prev.conditions, newCondition] };
      } else {
        const existing = prev.conditions[existingIdx];
        const currentValues = Array.isArray(existing.value) ? existing.value : [existing.value];

        if (currentValues.includes(value)) {
          // Remove value
          const newValues = currentValues.filter((v) => v !== value);
          if (newValues.length === 0) {
            // Remove condition entirely
            return removeCondition(prev, existing.id);
          }
          return updateCondition(prev, existing.id, { value: newValues });
        } else {
          // Add value
          return updateCondition(prev, existing.id, { value: [...currentValues, value] });
        }
      }
    });
  }, []);

  // Add new filter condition
  const handleAddCondition = useCallback(() => {
    const firstField = filterFields[0];
    if (!firstField)
      return;

    const newCondition = createCondition(
      firstField.field,
      getDefaultOperators(firstField.type)[0],
      firstField.type === 'enum' && firstField.multiple ? [] : '',
    );
    setFilters((prev) => ({ ...prev, conditions: [...prev.conditions, newCondition] }));
  }, [filterFields]);

  // Update a condition
  const handleUpdateCondition = useCallback((conditionId: string, updates: Partial<FilterCondition>) => {
    setFilters((prev) => updateCondition(prev, conditionId, updates));
  }, []);

  // Remove a condition
  const handleRemoveCondition = useCallback((conditionId: string) => {
    setFilters((prev) => removeCondition(prev, conditionId));
  }, []);

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setFilters(createEmptyFilterGroup());
    setSearchQuery('');
    setSelectedPresetName(null);
  }, []);

  // Toggle column visibility
  const handleToggleColumn = useCallback((field: string) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(field)) {
        next.delete(field);
      } else {
        next.add(field);
      }
      return next;
    });
  }, []);

  // Show all columns
  const handleShowAllColumns = useCallback(() => {
    setVisibleColumns(new Set(columns.map((col) => col.field)));
  }, [columns]);

  // Hide all columns
  const handleHideAllColumns = useCallback(() => {
    setVisibleColumns(new Set());
  }, []);

  // Apply a preset (from built-in or saved)
  const handleApplyPreset = useCallback((preset: FilterPreset) => {
    setFilters(preset.filters);
    setSelectedPresetName(preset.name);
    toast.success(`Applied preset: ${preset.name}`);
  }, []);

  // Apply filters from preset browser
  const handleApplyFilters = useCallback((filterGroup: FilterGroup, name: string) => {
    setFilters(filterGroup);
    setSelectedPresetName(name);
    toast.success(`Applied preset: ${name}`);
  }, []);

  // Save current filters as a preset
  const handleSavePreset = useCallback(async (name: string, presetDescription: string) => {
    setIsSaving(true);
    try {
      await savePresetMutation({
        name,
        description: presetDescription || undefined,
        reportType,
        filters: {
          logic: filters.logic,
          conditions: filters.conditions.map((c) => ({
            id: c.id,
            field: c.field,
            operator: c.operator,
            value: c.value,
            enabled: c.enabled,
          })),
        },
      });
      toast.success(`Saved preset: ${name}`);
      setSaveDialogOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save preset');
    } finally {
      setIsSaving(false);
    }
  }, [savePresetMutation, reportType, filters]);

  // Delete a saved preset
  const handleDeletePreset = useCallback(async (presetId: Id<'reportPresets'>) => {
    setIsDeleting(true);
    try {
      await deletePresetMutation({ presetId });
      toast.success('Preset deleted');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete preset');
    } finally {
      setIsDeleting(false);
    }
  }, [deletePresetMutation]);

  // Update a saved preset
  const handleUpdatePreset = useCallback(async (presetId: Id<'reportPresets'>, name: string, description: string) => {
    setIsUpdating(true);
    try {
      await updatePresetMutation({
        presetId,
        name,
        description: description || undefined,
      });
      toast.success('Preset updated');
      setEditingPreset(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update preset');
    } finally {
      setIsUpdating(false);
    }
  }, [updatePresetMutation]);

  // Toggle bookmark on a preset
  const handleToggleBookmark = useCallback(async (presetId: Id<'reportPresets'>) => {
    try {
      const result = await toggleBookmarkMutation({ presetId });
      toast.success(result.isBookmarked ? 'Preset bookmarked' : 'Bookmark removed');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to toggle bookmark');
    }
  }, [toggleBookmarkMutation]);

  // Toggle bookmark on a built-in preset
  const handleToggleBuiltInBookmark = useCallback(async (preset: FilterPreset) => {
    try {
      const result = await bookmarkBuiltInMutation({
        presetId: preset.id,
        name: preset.name,
        description: preset.description,
        reportType,
        filters: preset.filters,
      });
      toast.success(result.isBookmarked ? 'Preset bookmarked' : 'Bookmark removed');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to toggle bookmark');
    }
  }, [bookmarkBuiltInMutation, reportType]);

  // Export to Excel - only export visible columns
  const handleExport = useCallback(() => {
    if (filteredData.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Get only visible columns for export
    const exportColumns = columns.filter((col) => visibleColumns.has(col.field));

    if (exportColumns.length === 0) {
      toast.error('No columns selected for export');
      return;
    }

    try {
      // Transform data for export
      const exportRows = filteredData.map((row) => {
        const exportRow: Record<string, unknown> = {};
        for (const col of exportColumns) {
          const value = row[col.field];
          exportRow[col.headerName] = col.valueFormatter
            ? col.valueFormatter(value, row)
            : value;
        }
        return exportRow;
      });

      // Create workbook
      const wb = utils.book_new();
      const ws = utils.json_to_sheet(exportRows);

      // Set column widths
      ws['!cols'] = exportColumns.map((col) => ({ wch: col.width ? col.width / 7 : 15 }));

      utils.book_append_sheet(wb, ws, 'Report');

      // Generate filename with timestamp
      const timestamp = format(new Date(), 'yyyy-MM-dd_HHmm');
      const filename = `${defaultFilename}_${timestamp}.xlsx`;

      writeFile(wb, filename);
      toast.success(`Exported ${filteredData.length} rows (${exportColumns.length} columns) to ${filename}`);
    } catch {
      toast.error('Failed to export report');
    }
  }, [filteredData, columns, visibleColumns, defaultFilename]);

  const activeFilterCount = countActiveFilters(filters);

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-bold">{title}</h1>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={filteredData.length === 0}
            className="gap-2"
          >
            <FileSpreadsheet className="size-4" />
            Export (
            {filteredData.length}
            )
          </Button>
        </div>
      </div>

      {/* Quick Filters */}
      <Card className="border-dashed">
        <CardContent className="py-4">
          <div className="flex flex-col gap-4">
            {/* Quick filter chips */}
            <QuickFilterChips
              filterFields={filterFields}
              filters={filters}
              onToggleValue={handleQuickFilterToggle}
            />

            {/* Presets Combobox */}
            <div className="border-t pt-3">
              <div className="flex items-center gap-2">
                <FacetLabel>Presets</FacetLabel>
                <PresetsCombobox
                  builtInPresets={builtInPresets}
                  savedPresets={savedPresets as SavedPreset[] | undefined}
                  selectedPresetName={selectedPresetName}
                  onApplyBuiltIn={handleApplyPreset}
                  onApplySaved={(preset) => handleApplyFilters(preset.filters, preset.name)}
                  onEdit={setEditingPreset}
                  onDelete={handleDeletePreset}
                  onToggleBookmark={handleToggleBookmark}
                  onToggleBuiltInBookmark={handleToggleBuiltInBookmark}
                />
              </div>
            </div>

            {/* Filter controls bar */}
            <div className="flex items-center justify-between border-t pt-3">
              <div className="flex items-center gap-2">
                {/* Save Preset button - appears when filters are active */}
                {activeFilterCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSaveDialogOpen(true)}
                    className="gap-2 text-xs"
                  >
                    <Save className="size-3.5" />
                    Save Preset
                  </Button>
                )}

                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFilters}
                    className="gap-1.5 text-xs text-muted-foreground"
                  >
                    <RotateCcw className="size-3" />
                    Clear filters
                  </Button>
                )}

                {activeFilterCount === 0 && !savedPresets?.length && (
                  <span className="text-xs text-muted-foreground">
                    Click chips above to filter, or add advanced filters below
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3">
                <CopyCountButton count={filteredData.length} label="rows" />

                <Input
                  type="search"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-8 w-48 text-xs"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Filters Panel - Always visible */}
      <Card>
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <Filter className="size-4" />
              Advanced Filters
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
                  {activeFilterCount}
                </Badge>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Label htmlFor="filter-logic" className="text-xs text-muted-foreground">
                Match
              </Label>
              <Select
                value={filters.logic}
                onValueChange={(value) => setFilters((prev) => ({ ...prev, logic: value as 'and' | 'or' }))}
              >
                <SelectTrigger id="filter-logic" className="h-7 w-20 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="and" className="text-xs">All</SelectItem>
                  <SelectItem value="or" className="text-xs">Any</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-col gap-2">
            {filters.conditions.map((condition) => (
              <FilterConditionRow
                key={condition.id}
                condition={condition}
                field={filterFields.find((f) => f.field === condition.field)}
                allFields={filterFields}
                onUpdate={(updates) => handleUpdateCondition(condition.id, updates)}
                onRemove={() => handleRemoveCondition(condition.id)}
              />
            ))}

            <Button
              variant="outline"
              size="sm"
              onClick={handleAddCondition}
              className="mt-2 w-fit gap-2 text-xs"
            >
              <Plus className="size-3.5" />
              Add Filter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Column Visibility */}
      <Card className="border-dashed">
        <CardHeader className="py-3">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <Columns3 className="size-4" />
            Column Visibility
            <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
              {visibleColumns.size}
              {' '}
              /
              {columns.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <ColumnVisibilityChips
            columns={columns}
            visibleColumns={visibleColumns}
            onToggleColumn={handleToggleColumn}
            onShowAll={handleShowAllColumns}
            onHideAll={handleHideAllColumns}
          />
        </CardContent>
      </Card>

      {/* Data Grid */}
      <div className="flex-1">
        {isLoading
          ? (
              <Card className="flex h-full items-center justify-center">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Loader2 className="size-5 animate-spin" />
                  <span>Loading data...</span>
                </div>
              </Card>
            )
          : (
              <AGDataGrid
                columnDefs={columnDefs}
                rowData={filteredData}
                quickFilterText={searchQuery}
                getRowId={getRowId ? (params) => getRowId(params.data) : undefined}
                onGridReady={(params) => {
                  gridApiRef.current = params.api;
                }}
                overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No data matches the current filters</span>'
              />
            )}
      </div>

      {/* Save Preset Dialog */}
      <SavePresetDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        onSave={handleSavePreset}
        isSaving={isSaving}
      />

      {/* Preset Browser Dialog */}
      <PresetBrowserDialog
        open={browserDialogOpen}
        onOpenChange={setBrowserDialogOpen}
        presets={savedPresets as SavedPreset[] | undefined}
        builtInPresets={builtInPresets}
        onApply={handleApplyFilters}
        onDelete={handleDeletePreset}
        isDeleting={isDeleting}
      />

      {/* Edit Preset Dialog */}
      <EditPresetDialog
        preset={editingPreset}
        onClose={() => setEditingPreset(null)}
        onSave={handleUpdatePreset}
        isUpdating={isUpdating}
      />
    </div>
  );
}

export default ConvexReportBuilder;

export * from './filter-utils';
// Re-export types
export * from './types';
